import { useMemo, useState, useEffect } from 'react';
import '../../styles/Header.css';
import { getAssetPath } from '../../utils/paths';
import {
    RiHeart3Line,
    RiSearchLine,
    RiShoppingBag2Line,
    RiUserLine,
    RiMenuLine,
    RiCloseLine,
    RiUserFill
} from 'react-icons/ri';
import { Tooltip } from 'react-tooltip'
import { Link } from 'react-router';
import { useNavigate } from "react-router-dom";
import { useAccount } from '../../contexts/AccountContext';
import { useApiCall } from '../../hooks/useApiCall';
import { useCounts } from '../../hooks/useCounts';


function Header({ onClick }) {
    const logo = getAssetPath("logos/logo_color.png");
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [hoverTimeout, setHoverTimeout] = useState(null);
    const { account } = useAccount();
    const navigate = useNavigate();
    const apiCall = useApiCall();

    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [headerPages, setHeaderPages] = useState([]);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showSearchResults, setShowSearchResults] = useState(false);
    
    // Use the custom hook for counts
    const { cartCount, wishlistCount } = useCounts();


    // Fetch categories on component mount
    useEffect(() => {
        // Create URL-friendly slug from category name
        const createSlug = (name) => {
            return name
                .toLowerCase()
                .replace(/[^a-z0-9\s&]/g, '') // Remove special characters except spaces and &
                .replace(/\s*&\s*/g, '-and-') // Replace & with -and-
                .replace(/\s+/g, '-') // Replace spaces with hyphens
                .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
                .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
        };

        // Transform API categories to match component format
        const transformCategories = (apiCategories) => {
            return apiCategories.map(category => ({
                id: category.id,
                name: category.name,
                slug: createSlug(category.name),
                items: category.children ? transformCategories(category.children) : undefined
            }));
        };

        const fetchCategories = async () => {
            try {
                setCategoriesLoading(true);
                const response = await apiCall('categories', 'POST', {}, null, null);
                
                if (response.success) {
                    // Transform the API response to match the expected format
                    const transformedCategories = transformCategories(response.data.categories);
                    setCategories(transformedCategories);
                } else {
                    console.error('Failed to fetch categories:', response.message);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
            } finally {
                setCategoriesLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Fetch pages assigned to the header placement
    useEffect(() => {
        apiCall('pages/get-by-placement', 'POST', { placement: 'header' })
            .then((response) => {
                if (response && response.success && response.data) {
                    setHeaderPages(response.data.pages || []);
                }
            })
            .catch((err) => console.error('Error fetching header pages:', err));
    }, []);

    // Search handler with debouncing
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                setSearchLoading(true);
                apiCall('products/search', 'POST', { 
                    query: searchQuery,
                    limit: 8
                }).then((response) => {
                    if (response && response.success) {
                        setSearchResults(response.data.products);
                        setShowSearchResults(true);
                    }
                    setSearchLoading(false);
                });
            } else {
                setSearchResults([]);
                setShowSearchResults(false);
            }
        }, 600); // 300ms debounce

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Handle Enter key press to navigate to products search page
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter' && searchQuery.trim().length >= 2) {
            navigate(`/products?query=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
            setShowSearchResults(false);
        }
    };

    // Handle search result click
    const handleSearchResultClick = (product) => {
        navigate(`/products/product/${product.id}/${product.slug}`);
        setSearchQuery('');
        setShowSearchResults(false);
    };

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.header-search-wrapper')) {
                setShowSearchResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const categoriesView = useMemo( () => {

        const handleCategoryMouseEnter = (categoryId) => {
            // Clear any existing timeout to prevent closing
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
            }
            // Immediately show the new category
            setHoveredCategory(categoryId);
        };

        const handleWrapperMouseLeave = () => {
            // Add a delay before closing the dropdown when leaving the entire wrapper
            const timeout = setTimeout(() => {
                setHoveredCategory(null);
            }, 600); // 600ms delay
            setHoverTimeout(timeout);
        };

        const handleWrapperMouseEnter = () => {
            // Clear timeout when re-entering the wrapper
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
            }
        };

        return (
            <div 
                className="header-categories-wrapper"
                onMouseLeave={handleWrapperMouseLeave}
                onMouseEnter={handleWrapperMouseEnter}
            >
                {/* Desktop Categories */}
                <div className={`header-categories ${mobileMenuOpen ? 'mobile-hidden' : ''}`}>
                    {categoriesLoading ? (
                        <div className="categories-loading">Loading categories...</div>
                    ) : categories.length > 0 ? (
                        categories.map( (category) => (
                        <button
                            onClick={
                                () => {
                                    navigate(`/products/category/${category.id}/${category.slug}`)
                                }
                            }  
                            key={category.id} 
                            className={`header-category ${hoveredCategory === category.id ? 'active' : ''}`}
                            onMouseEnter={() => handleCategoryMouseEnter(category.id)}
                        >
                            { category.name }
                        </button>
                        ))
                    ) : (
                        <div className="categories-empty">No categories available</div>
                    )}
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <>
                        <div 
                            className="header-mobile-overlay"
                            onClick={() => setMobileMenuOpen(false)}
                        />
                        <div className="header-mobile-menu">
                            <div className="header-mobile-menu-content">
                            {categories.map((category) => (
                                <div key={category.id} className="mobile-category-section">
                                    <button 
                                        className="mobile-category-title"
                                        onClick={() => {
                                            navigate(`/products/category/${category.id}/${category.slug}`);
                                            setMobileMenuOpen(false);
                                        }}
                                    >
                                        {category.name}
                                    </button>
                                    {category.items && (
                                        <div className="mobile-subcategories">
                                            {category.items.map((subCategory) => (
                                                <div key={subCategory.id} className="mobile-subcategory">
                                                    <button 
                                                        className="mobile-subcategory-title"
                                                        onClick={() => {
                                                            navigate(`/products/category/${subCategory.id}/${subCategory.slug}`);
                                                            setMobileMenuOpen(false);
                                                        }}
                                                    >
                                                        {subCategory.name}
                                                    </button>
                                                    {subCategory.items && (
                                                        <div className="mobile-sub-items">
                                                            {subCategory.items.map((item) => (
                                                                <button
                                                                    key={item.id}
                                                                    className="mobile-sub-item"
                                                                    onClick={() => {
                                                                        navigate(`/products/category/${item.id}/${item.slug}`);
                                                                        setMobileMenuOpen(false);
                                                                    }}
                                                                >
                                                                    {item.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    </>
                )}
                
                {/* Dropdown for subcategories */}
                {hoveredCategory && (
                    <div 
                        className="header-dropdown"
                    >
                        <div className="header-dropdown-content">
                            {(() => {
                                const activeCategory = categories.find(cat => cat.id === hoveredCategory);
                                if (!activeCategory || !activeCategory.items) return null;
                                
                                return activeCategory.items.map((subCategory) => (
                                    <div key={subCategory.id} className="header-dropdown-column">
                                        <button onClick={() => navigate(`/products/category/${subCategory.id}/${subCategory.slug}`)} className="header-dropdown-title">
                                            {subCategory.name}
                                            <span className="header-dropdown-arrow">→</span>
                                        </button>
                                        {subCategory.items && (
                                            <div className="header-dropdown-items">
                                                {subCategory.items.map((subItem) => (
                                                    <button 
                                                        key={subItem.id} 
                                                        className="header-dropdown-item"
                                                        onClick={() => navigate(`/products/category/${subItem.id}/${subItem.slug}`)}
                                                    >
                                                        {subItem.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                )}
            </div>
        )

    }, [categories, hoveredCategory, mobileMenuOpen, navigate, categoriesLoading, hoverTimeout, setHoverTimeout])

  return (
    <div className="header-wrapper">
        <Tooltip id="header-tooltip" />

        <div className="header-sub">
            
            <Link to ="/contact" className="header-sub-link">
                Contact
            </Link>
            <Link to ="/about-us" className="header-sub-link">
                About Us
            </Link>
            <Link to ="/faq" className="header-sub-link">
                FAQ
            </Link>
            {headerPages.map((page) => (
                <Link key={page.id} to={`/page/${page.slug}`} className="header-sub-link">
                    {page.title}
                </Link>
            ))}

        </div>
        <div className="header-sticky">
            <div className="header">

                <button onClick={() => navigate("/")} className="header-logo">
                    <img src={logo} alt={'BDS logo'} title="Building Depot Suriname: Lights and Living" />
                </button>

                <div className="header-search-wrapper">

                    <div className="header-search">
                        <button className="header-search-button" title="Search">
                            <RiSearchLine size={18} />
                        </button>
                        <input 
                            type="text" 
                            className="header-search-input" 
                            placeholder="Search products, categories..." 
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                            onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                        />
                    </div>

                    {/* Search Results Dropdown */}
                    {showSearchResults && searchResults.length > 0 && (
                        <div className="header-search-results">
                            {searchResults.map((product) => (
                                <button
                                    key={product.id}
                                    className="header-search-result-item"
                                    onClick={() => handleSearchResultClick(product)}
                                >
                                    {product.image && (
                                        <img 
                                            src={product.image} 
                                            alt={product.name}
                                            className="search-result-image"
                                        />
                                    )}
                                    <div className="search-result-details">
                                        <div className="search-result-name">{product.name}</div>
                                        {product.brand && (
                                            <div className="search-result-brand">{product.brand}</div>
                                        )}
                                        <div className="search-result-price">
                                            {product.sale_price > 0 ? (
                                                <>
                                                    <span className="price-sale">{product.price.curr} {product.sale_price.toFixed(2)}</span>
                                                    <span className="price-original">{product.price.curr} {product.price.amount.toFixed(2)}</span>
                                                </>
                                            ) : (
                                                <span className="price">{product.price.curr} {product.price.amount.toFixed(2)}</span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {showSearchResults && searchQuery.trim().length >= 2 && searchResults.length === 0 && !searchLoading && (
                        <div className="header-search-results">
                            <div className="header-search-no-results">
                                No products found for "{searchQuery}"
                            </div>
                        </div>
                    )}

                </div>
                
                <div className="header-account">
                    <Link to={ account ? '/account' : '/sign-in'} className="header-account-button" title={ account ? 'Account' : 'Sign in'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content={ account ? "Manage your account" : "Sign in to your account"}>
                        { account ?
                        <div className='header-account-avatar' style={{backgroundImage: `url(${account.avatar || getAssetPath('default_avatar.png')})`}}></div>
                        :
                        <RiUserLine size={20} />
                        }
                    </Link>
                    <Link to={ account ? '/wishlist' : '/sign-in?ref=wishlist'} className="header-account-button" title={'Wishlist'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="View your wishlist" style={{ position: 'relative' }}>
                        <RiHeart3Line size={20} />
                        {wishlistCount > 0 && (
                            <span className="header-badge">{wishlistCount}</span>
                        )}
                    </Link>
                    <Link to={ account ? '/cart' : '/sign-in?ref=cart'} className="header-account-button" title={'Shopping cart'} data-tooltip-id="header-tooltip" data-tooltip-place='left' data-tooltip-content="View your shopping cart" style={{ position: 'relative' }}>
                        <RiShoppingBag2Line size={20} />
                        {cartCount > 0 && (
                            <span className="header-badge">{cartCount}</span>
                        )}
                    </Link>
                    
                    {/* Mobile menu toggle - rightmost position */}
                    <button 
                        className="header-mobile-toggle"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        title="Menu"
                    >
                        {mobileMenuOpen ? <RiCloseLine /> : <RiMenuLine />}
                    </button>
                </div>
            
            </div>

            { categoriesView }
        </div>
    </div>
  );
}

export default Header;
